import React, { useMemo } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SECTORS, SERVICES, PRESENCE_FIELDS, STAGES } from "../../lib/constants";
import { Instagram } from "lucide-react";

export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

const TEXT_FIELDS = [
  ["referente", "Referente"], ["citta", "Città"], ["telefono", "Telefono"], ["email", "Email"],
  ["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"], ["sito", "Sito web"],
];

export const BaseFields = ({ form, set }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Azienda *"><Input data-testid="input-azienda" value={form.azienda} onChange={(e) => set("azienda", e.target.value)} /></Field>
      <Field label="Settore">
        <Select value={form.settore} onValueChange={(v) => set("settore", v)}>
          <SelectTrigger data-testid="select-settore"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {TEXT_FIELDS.map(([k, l]) => (
        <Field key={k} label={l}><Input value={form[k]} onChange={(e) => set(k, e.target.value)} /></Field>
      ))}
    </div>
    <Field label="Note"><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} /></Field>
  </>
);

const IG_FIELDS = [
  ["followers", "Follower", "ig-followers"], ["following", "Seguiti", "ig-following"], ["posts", "Post totali", "ig-posts"],
  ["posts_per_week", "Post / settimana", "ig-ppw"], ["avg_likes", "Like medi/post", "ig-likes"], ["avg_comments", "Commenti medi/post", "ig-comments"],
];

export const PresenceFields = ({ form, setDp, setIg }) => (
  <>
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div className="mb-1 flex items-center gap-2 label-caps text-primary">
        <Instagram className="h-3.5 w-3.5" /> Dati Instagram reali (opzionali)
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Inserisci i numeri veri del profilo: l'analisi AI userà questi dati invece delle stime.</p>
      <div className="grid grid-cols-3 gap-3">
        {IG_FIELDS.map(([k, l, tid]) => (
          <Field key={k} label={l}><Input type="number" value={form.ig_stats[k]} onChange={(e) => setIg(k, e.target.value)} data-testid={tid} /></Field>
        ))}
      </div>
    </div>
    <p className="text-xs text-muted-foreground">Valuta ogni canale da 0 (assente/debole) a 10 (eccellente). Punteggi bassi = più opportunità.</p>
    {PRESENCE_FIELDS.map((f) => <PresenceSlider key={f.key} field={f} value={form.digital_presence[f.key]} onChange={setDp} />)}
  </>
);

function PresenceSlider({ field, value, onChange }) {
  const sliderValue = useMemo(() => [value], [value]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span>{field.label}</span>
        <span className="font-heading tabular-nums text-primary">{value}/10</span>
      </div>
      <Slider value={sliderValue} min={0} max={10} step={1} onValueChange={(v) => onChange(field.key, v[0])} data-testid={`slider-${field.key}`} />
    </div>
  );
}

export const PipelineFields = ({ form, set }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Servizio consigliato">
        <Select value={form.servizio_consigliato} onValueChange={(v) => set("servizio_consigliato", v)}>
          <SelectTrigger data-testid="select-servizio"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Budget potenziale"><Input placeholder="es. €500-800/mese" value={form.budget_potenziale} onChange={(e) => set("budget_potenziale", e.target.value)} /></Field>
      <Field label="Valore potenziale (€)"><Input type="number" value={form.valore_potenziale} onChange={(e) => set("valore_potenziale", e.target.value)} /></Field>
      <Field label="Stato pipeline">
        <Select value={form.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
          <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Responsabile"><Input value={form.responsabile} onChange={(e) => set("responsabile", e.target.value)} /></Field>
      <Field label="Data prossima azione"><Input type="date" value={(form.next_action_date || "").slice(0, 10)} onChange={(e) => set("next_action_date", e.target.value)} /></Field>
    </div>
    <Field label="Problema principale"><Textarea value={form.problema_principale} onChange={(e) => set("problema_principale", e.target.value)} rows={2} /></Field>
    <Field label="Prossima azione"><Input placeholder="es. Chiamare alle 15:30" value={form.next_action} onChange={(e) => set("next_action", e.target.value)} /></Field>
  </>
);
